import { useState, useEffect, useRef, useCallback } from 'react';
import { MAPBOX_CONFIG } from '../components/map/mapbox.config';

const DIRECTIONS_URL = 'https://api.mapbox.com/directions/v5/mapbox/driving';

/**
 * Builds vehicle params for TomTom based on vehicleType.
 */
function getTomTomVehicleParams(vehicleType) {
  const type = (vehicleType || '').toUpperCase();
  const isHeavy = type === 'HEAVY' || type.includes('LOURD');
  return {
    weight: isHeavy ? '26000' : '7500',
    height: isHeavy ? '4.5' : '3.5',
  };
}

/**
 * Fetches a route from TomTom or Mapbox.
 * Supports an optional waypoint (e.g. the pickup point).
 *
 * @param {number} fromLat
 * @param {number} fromLng
 * @param {number} toLat
 * @param {number} toLng
 * @param {object|null} waypointLatLng  Optional { lat, lng } stop between from and to
 * @param {string} vehicleType
 * @returns {{ durationSeconds, distanceKm } | null}
 */
async function fetchRoute(fromLat, fromLng, toLat, toLng, waypointLatLng = null, vehicleType = 'HEAVY') {
  const tomtomKey = process.env.REACT_APP_TOMTOM_API_KEY;

  if (tomtomKey) {
    try {
      const { weight, height } = getTomTomVehicleParams(vehicleType);

      // Build coordinate string: origin[:waypoint]:destination
      let coords = `${fromLat},${fromLng}`;
      if (waypointLatLng) {
        coords += `:${waypointLatLng.lat},${waypointLatLng.lng}`;
      }
      coords += `:${toLat},${toLng}`;

      const params = new URLSearchParams({
        key: tomtomKey,
        routeType: 'fastest',
        traffic: 'true',
        travelMode: 'truck',
        vehicleMaxSpeed: '90',
        vehicleCommercial: 'true',
        vehicleWeight: weight,
        vehicleLength: '10',
        vehicleWidth: '2.5',
        vehicleHeight: height,
      });

      const url = `https://api.tomtom.com/routing/1/calculateRoute/${coords}/json?${params}`;
      const res = await fetch(url);
      if (res.ok) {
        const data = await res.json();
        const routes = data.routes;
        if (routes && routes.length > 0) {
          const summary = routes[0].summary;
          return {
            durationSeconds: summary.travelTimeInSeconds,
            distanceKm: summary.lengthInMeters / 1000,
          };
        }
      }
    } catch (e) {
      console.warn('[useEtaCalculator] TomTom routing error', e);
    }
  }

  // ── Mapbox fallback (no waypoint support in driving profile for trucks) ──
  const token = MAPBOX_CONFIG.ACCESS_TOKEN;
  if (!token) return null;

  // Build Mapbox coordinate string: lon,lat;[waypoint_lon,waypoint_lat;]lon,lat
  let coordStr = `${fromLng},${fromLat}`;
  if (waypointLatLng) {
    coordStr += `;${waypointLatLng.lng},${waypointLatLng.lat}`;
  }
  coordStr += `;${toLng},${toLat}`;

  const url = `${DIRECTIONS_URL}/${coordStr}?overview=false&steps=false&access_token=${token}`;
  const res = await fetch(url);
  if (!res.ok) return null;
  const data = await res.json();
  const route = data.routes?.[0];
  if (!route) return null;
  return {
    durationSeconds: route.duration,
    distanceKm: route.distance / 1000,
  };
}

/**
 * Standalone function to calculate ETA.
 */
export async function calculateMissionEta({
  driverLat, driverLng,
  pickupLat, pickupLng,
  destLat, destLng,
  missionStarted = false,
  driverSpeed,
  vehicleType,
}) {
  if (!destLat || !destLng) throw new Error('Missing destination coordinates');

  let result = null;

  if (missionStarted) {
    if (!driverLat || !driverLng) throw new Error('Missing driver coordinates for active mission');
    result = await fetchRoute(driverLat, driverLng, destLat, destLng, null, vehicleType);
  } else {
    const hasDriverPos = !!(driverLat && driverLng);
    const hasPickup    = !!(pickupLat && pickupLng);

    if (hasDriverPos && hasPickup) {
      result = await fetchRoute(
        driverLat, driverLng,
        destLat, destLng,
        { lat: pickupLat, lng: pickupLng },
        vehicleType,
      );
    } else if (hasPickup) {
      result = await fetchRoute(pickupLat, pickupLng, destLat, destLng, null, vehicleType);
    } else if (hasDriverPos) {
      result = await fetchRoute(driverLat, driverLng, destLat, destLng, null, vehicleType);
    }
  }

  if (!result) {
    throw new Error('Route unavailable');
  }

  const totalDistanceKm = result.distanceKm;
  let mins;

  if (result.durationSeconds > 0) {
    mins = Math.round(result.durationSeconds / 60);
  } else {
    const speedKmh = (driverSpeed || 0) * 3.6;
    const effectiveSpeed = speedKmh > 10 ? speedKmh : 50;
    mins = Math.round((totalDistanceKm / effectiveSpeed) * 60);
  }

  const arrival = new Date(Date.now() + mins * 60 * 1000);
  const arrivalStr = `${arrival.getHours().toString().padStart(2, '0')}:${arrival.getMinutes().toString().padStart(2, '0')}`;

  return {
    remainingMinutes: mins,
    estimatedArrival: arrivalStr,
    distanceKm: parseFloat(totalDistanceKm.toFixed(1))
  };
}

/**
 * Hook that calculates live ETA for a mission.
 *
 * Modes:
 *  - EN_COURS  (missionStarted=true):  driver position → delivery
 *  - EN_ATTENTE (missionStarted=false): driver position → pickup → delivery  (full trip estimate)
 *    If driverLat/Lng are not available, falls back to pickup → delivery only.
 *
 * @param {object} params
 * @param {number|null} params.driverLat      Driver latitude
 * @param {number|null} params.driverLng      Driver longitude
 * @param {number|null} params.pickupLat      Pickup latitude  (mission.departureLat)
 * @param {number|null} params.pickupLng      Pickup longitude (mission.departureLng)
 * @param {number|null} params.destLat        Delivery latitude  (mission.arrivalLat)
 * @param {number|null} params.destLng        Delivery longitude (mission.arrivalLng)
 * @param {boolean}     params.missionStarted True when mission is EN_COURS
 * @param {number|null} params.driverSpeed    Driver speed in m/s (from backend position)
 * @param {string|null} params.vehicleType    Vehicle type (HEAVY / LIGHT)
 * @param {boolean}     [params.enabled]      Set false to disable
 *
 * @returns {{ remainingMinutes, estimatedArrival, distanceKm, loading, error }}
 */
export function useEtaCalculator({
  driverLat, driverLng,
  pickupLat, pickupLng,
  destLat, destLng,
  missionStarted = false,
  driverSpeed,
  vehicleType,
  enabled = true,
}) {
  const [remainingMinutes, setRemainingMinutes] = useState(null);
  const [estimatedArrival, setEstimatedArrival] = useState(null);
  const [distanceKm, setDistanceKm] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const isMounted = useRef(true);

  const calculate = useCallback(async () => {
    if (!enabled || !destLat || !destLng) return;

    setLoading(true);
    setError(null);

    try {
      const result = await calculateMissionEta({
        driverLat, driverLng,
        pickupLat, pickupLng,
        destLat, destLng,
        missionStarted,
        driverSpeed,
        vehicleType
      });

      if (!isMounted.current) return;

      setRemainingMinutes(result.remainingMinutes);
      setEstimatedArrival(result.estimatedArrival);
      setDistanceKm(result.distanceKm);
    } catch (err) {
      if (isMounted.current) setError(err.message);
    } finally {
      if (isMounted.current) setLoading(false);
    }
  }, [driverLat, driverLng, pickupLat, pickupLng, destLat, destLng, missionStarted, driverSpeed, vehicleType, enabled]);

  useEffect(() => {
    isMounted.current = true;
    calculate();
    return () => { isMounted.current = false; };
  }, [calculate]);

  return { remainingMinutes, estimatedArrival, distanceKm, loading, error };
}
